/**
 * setMath — per-set DISPLAY math over INCLUDED laps only.
 *
 * Set membership, meters, durationSeconds, and label stay watch-authoritative
 * (setGroups assigns ALL laps deliberately — see its module doc); the detail
 * sheet's chip row, length cards, and title meta line keep rendering every
 * member. But the numbers the sheet CLAIMS about a set — Best/Slowest, average
 * pace, the effort-badge HR input — must never be driven by a lap the
 * exclusion passes ruled out (rest/wall, slow outlier, phantom split-length
 * artifact). The live UAT bug this kills: "Best: 9.2s Butterfly" from a
 * physically impossible phantom length.
 *
 * Pure module (no Svelte runtime), per lib convention. Imports only types, so
 * setStory can import from here without a cycle.
 */
import type { Lap } from "./laps";
import type { SetGroup } from "./setGroups";
import type { HrPoint } from "./setHrWindow";

/** Member laps that survive exclusion — the population for every set claim. */
export function includedLaps(set: SetGroup): Lap[] {
  return set.laps.filter((l) => !l.effectiveExcluded);
}

/**
 * Best / slowest INCLUDED lap by duration. Null when fewer than 2 included
 * laps — preserves the current lone-length rule, and an all-excluded set
 * renders nothing rather than lying.
 */
export function setBestWorst(set: SetGroup): { fastest: Lap; slowest: Lap } | null {
  const laps = includedLaps(set);
  if (laps.length < 2) return null;
  let fastest = laps[0];
  let slowest = laps[0];
  for (const l of laps) {
    if (l.durationSeconds < fastest.durationSeconds) fastest = l;
    if (l.durationSeconds > slowest.durationSeconds) slowest = l;
  }
  return { fastest, slowest };
}

/**
 * Average pace per length, seconds — sum of included durations / included
 * count. Null when fewer than 2 included laps (one length has no "average").
 */
export function setAvgPaceSeconds(set: SetGroup): number | null {
  const laps = includedLaps(set);
  if (laps.length < 2) return null;
  const total = laps.reduce((s, l) => s + l.durationSeconds, 0);
  return total / laps.length;
}

/**
 * Set-average HR over included laps, skipping null HR; null when none carry
 * HR. Feeds setEffortBadge — a phantom lap's HR must not color the badge.
 */
export function setAvgHrIncluded(set: SetGroup): number | null {
  const hrs = includedLaps(set)
    .map((l) => l.avgHr)
    .filter((h): h is number => h !== null);
  return hrs.length === 0 ? null : hrs.reduce((s, h) => s + h, 0) / hrs.length;
}

/**
 * True when the set has members but every one is excluded. The sheet then
 * renders ONLY the "These lengths look off…" honesty line in the story card —
 * no badge, no Best/avg, no sparkline, and crucially no no-HR line (an
 * all-excluded set has null set-level stats even on a swim full of HR).
 */
export function allLapsExcluded(set: SetGroup): boolean {
  return set.laps.length > 0 && includedLaps(set).length === 0;
}

/**
 * The single honest line an all-excluded set's story card carries. Singular
 * for a lone length so the sentence never disagrees with the "1 length" meta
 * line right above it (panel gate on 8c72b12). Jargon-free: never "phantom" /
 * "artifact" / "excluded"; "averages and bests" names the concrete stats.
 */
export function looksOffLine(set: SetGroup): string {
  return set.isLoneLength
    ? "This length looks off, so it doesn't count toward your averages and bests."
    : "These lengths look off, so they don't count toward your averages and bests.";
}

/**
 * The honest no-HR guard: fires ONLY when the whole SWIM lacks HR samples.
 * Keying this off set-level stats is the bug this kills — the solo swim's
 * lone-phantom Warmup nulled every set stat and the sheet claimed "No heart
 * rate recorded" on a swim carrying 361 real samples.
 */
export function swimLacksHeartRate(hrSeries: readonly HrPoint[]): boolean {
  return hrSeries.length === 0;
}

/**
 * Start and end ms timestamps for a set, for the header time-of-day line.
 * Returns null when the set is falsy or either bound is non-finite (guard against
 * a synthetic set whose laps have NaN timestamps).
 */
export function setTimeRange(set: SetGroup | null | undefined): { startMs: number; endMs: number } | null {
  if (!set) return null;
  if (!Number.isFinite(set.startMs) || !Number.isFinite(set.endMs)) return null;
  return { startMs: set.startMs, endMs: set.endMs };
}

/** 0-based index of the set group containing the 1-based lap index, or null.
 *  The suspects card uses this to open the right SetDetailSheet. */
export function setIndexForLap(setGroups: SetGroup[], lapIndex: number): number | null {
  const idx = setGroups.findIndex((g) => g.laps.some((l) => l.index === lapIndex));
  return idx === -1 ? null : idx;
}
